
import Vue from 'vue'
import Component from 'vue-class-component'
import userInfoStore from '@/store/userInfo'
import CatalogsService from '@/services/CatalogsService'
import CatalogsFillsService from '@/services/CatalogsFillsService'
import { PageRequest } from '@/types/Support'
import { PermissionNames } from '@/types/Auth'
import { BasicEntity, CatalogGridItemColumn, CrudActions, Domain, DomainCatalogFilters } from '@/types/Catalogs'
import { Catalog, CatalogCatalogFilters, CatalogCatalogGridItem } from '@/types/catalogs/Catalogs'
import InputSelectorFilter from '@/components/generic/InputSelectorFilter.vue'
import TopToolBarPaginator from '@/components/generic/TopToolBarPaginator.vue'
import { ProjectCatalogFilters } from '@/types/catalogs/Projects'
import { MessageError } from '@/components/generic/MessageHandling.vue'
import ReportsService from '@/services/ReportsService'
import CatalogForm from '@/components/project/CatalogForm.vue'
import DateFormat from '@/services/DateFormat'

@Component({
  name: 'ProjectCatalogs',
  components: {
    InputSelectorFilter,
    TopToolBarPaginator,
    CatalogForm
  }
})
export default class ProjectCatalogs extends Vue {
  /* General */
  PAGE_SIZE_MAX: number = 500
  PAGE_SIZE_CATALOGS: number = 100
  PAGE_SIZE: number = 50
  PAGE_VISIBLE_ITEMS: number = this.PAGE_SIZE
  visibleItems: number[] = [0, this.PAGE_VISIBLE_ITEMS]
  paginatorKey: number = 0
  currentPageIndex: number = 1
  crudActions: any = CrudActions
  currentAction: CrudActions = CrudActions.READ
  leftSidebarCollapsed: boolean = false
  deleteConfirmation: boolean = false
  groupingConfirmation: boolean = false
  sameGroupingSelectedItem: boolean = false

  /* Loading flags */
  loadingData: boolean = false
  loadingDomains: boolean = false
  loadingProjects: boolean = false

  /* For filters */
  filters: CatalogCatalogFilters = new CatalogCatalogFilters()
  defaultDomain: BasicEntity = new BasicEntity(-1, '')
  domains: BasicEntity[] = []
  projects: any[] = []
  projectFilters: ProjectCatalogFilters = new ProjectCatalogFilters()
  inheritProjects: any[] = []
  sortOrderOptions: any = [
    { value: 'id', text: 'ID' },
    { value: 'name', text: 'Nombre' },
    { value: 'version', text: 'Versión' },
    { value: 'creationDate', text: 'Fecha de creación' },
    { value: 'updateDate', text: 'Fecha de actualización' }
  ]
  /* Entities to list view */
  allCatalogItems: CatalogCatalogGridItem[] = []
  items: CatalogCatalogGridItem[] = []
  selectedItem: CatalogCatalogGridItem = new CatalogCatalogGridItem()
  selectedGroupingItem: CatalogCatalogGridItem = new CatalogCatalogGridItem()
  totalItems: number = 0
  columns: CatalogGridItemColumn[] = [
    CatalogGridItemColumn.def('name', 'Nombre', true),
    CatalogGridItemColumn.def('id', 'ID', true),
    CatalogGridItemColumn.def('version', 'Versión'),
    CatalogGridItemColumn.def('description', 'Descripción'),
    CatalogGridItemColumn.def('source', 'Fuente'),
    CatalogGridItemColumn.def('creationDate', 'Fecha de creación'),
    CatalogGridItemColumn.def('updateDate', 'Fecha de actualización'),
    CatalogGridItemColumn.def('grouping', 'Agrupamiento'),
    CatalogGridItemColumn.def('generalDomain', 'General en dominio')
  ]
  readonly: boolean = true

  /**
   * @returns true if there is one row selected
   */
  get existItemSelected () {
    return this.selectedItem.id >= 0
  }

  /**
   * @returns true if the user role has the permission
   * to read catalogs
   */
  get canReadCatalogs () {
    return userInfoStore.hasPermissions([
      PermissionNames.CATALOGS_READ,
      PermissionNames.PROJECTS_READ
    ])
  }

  /**
   * @returns true if the user role has the permission
   * to create catalogs
   */
  get canCreateCatalogs () {
    return userInfoStore.hasPermissions([
      PermissionNames.CATALOGS_CREATE,
      PermissionNames.PROJECTS_READ
    ])
  }

  /**
   * @returns true if the user role has the permission
   * to grouping catalogs
   */
  get canGroupingCatalgos () {
    return userInfoStore.hasPermissions([
      PermissionNames.CATALOGS_UPDATE,
      PermissionNames.CATALOGS_GROUPING
    ])
  }

  /**
   * @returns true if the user role has the permission
   * to update catalogs
   */
  get canUpdateCatalogs () {
    return userInfoStore.hasPermissions([
      PermissionNames.CATALOGS_UPDATE,
      PermissionNames.PROJECTS_READ
    ])
  }

  /**
   * @returns true if the user role has the permission
   * to delete catalogs
   */
  get canDeleteCatalogs () {
    return userInfoStore.hasPermissions([
      PermissionNames.CATALOGS_DELETE,
      PermissionNames.PROJECTS_READ
    ])
  }

  /** @returns true if the last query has a positive lenght, false otherwise */
  get isEmptyCatalogs () {
    return this.totalItems <= 0
  }

  /** Lifecycle from the component */
  async created () {
    this.defaultDomain = userInfoStore.userInfo.domain
    this.filters.domain = this.defaultDomain
    if (this.showProjectFilter) {
      this.columns.splice(2, 0, CatalogGridItemColumn.def('projectName', 'Proyecto'))
      await this.loadProjects()
    }
    if (this.showDomainFilter) {
      this.columns.splice(2, 0, CatalogGridItemColumn.def('domainName', 'Dominio'))
      await this.getDomains(new PageRequest<DomainCatalogFilters>(this.PAGE_SIZE_MAX))
      this.projectFilters.domain = this.defaultDomain.id
    }
    this.loadData(true)
  }

  /**
   * Prepares and loads the catalog records for the main view
   */
  loadData (updateTotalItems: boolean) {
    if (this.loadingData) return
    this.items = []
    let pageRequest = new PageRequest<CatalogCatalogFilters>()
    pageRequest.size = this.PAGE_SIZE
    pageRequest.page = this.currentPageIndex - 1
    pageRequest.filters = this.filters
    this.selectItem(new CatalogCatalogGridItem())
    this.getCatalogs(pageRequest, updateTotalItems)
    this.getLastGroupingCatalog()
  }

  /**
   * Prepares and loads the project records for the project filter
   */
  async loadProjects () {
    if (this.loadingProjects) return
    this.projects = []
    let pageRequest = new PageRequest<ProjectCatalogFilters>()
    pageRequest.size = this.PAGE_SIZE
    pageRequest.page = this.currentPageIndex - 1
    pageRequest.filters = this.projectFilters
    await this.getProjects(pageRequest)
      .then(() => {
        if (this.showProjectFilter &&
          this.projects.every((p: BasicEntity) => p.id !== -1)) {
          let descriptionItem = !this.projects.length
            ? '-- Sin projectos --'
            : '-- Seleccionar proyecto --'
          this.projects.unshift(
            { 'value': -1, 'text': descriptionItem }
          )
        }
      })
  }

  /**
   * @returns true if the user belongs to root domain and
   * has the permission to read domains, false otherwise
   */
  get showDomainFilter () {
    return userInfoStore.inRootDomain() &&
      userInfoStore.hasPermission(PermissionNames.DOMAINS_READ)
  }

  /**
   * @returns true if the user belongs to root domain and
   * has the permission to read projects, false otherwise
   */
  get showProjectFilter () {
    return userInfoStore.hasPermission(PermissionNames.PROJECTS_READ)
  }

  getTextItemColum (col: CatalogGridItemColumn, item: CatalogCatalogGridItem): any {
    if (col.key === 'grouping') {
      return item[col.key] ? 'Agrupado' : ''
    }
    if (col.key === 'generalDomain') {
      return item[col.key] ? 'Sí' : ''
    }
    return (item as any)[col.key]
  }
  /**
   * Creates a catalog item for the rows in the table view
   *
   * @param catalog entity with the values
   */
  createCatalogGridItem (catalog: Catalog) {
    let catalogItem = new CatalogCatalogGridItem()
    Object.assign(catalogItem, catalog)
    catalogItem.creationDate = catalog.creationDate ? new DateFormat(catalog.creationDate).toDateString() : '- - / - - / - - - -'
    catalogItem.updateDate = catalog.updateDate ? new DateFormat(catalog.updateDate).toDateString() : '- - / - - / - - - -'
    return catalogItem
  }

  /**
   * Gets the catalogs for the current page
   * and sets it in items variable
   *
   * @param pageRequest with the request configurations
   * @param updateTotalItem if the catalogs counter must be updated
   */
  async getCatalogs (pageRequest: PageRequest<CatalogCatalogFilters>, updateTotalItems: boolean) {
    this.loadingData = true
    CatalogsFillsService.getCatalogs(pageRequest)
      .then((response) => {
        if (updateTotalItems) {
          this.totalItems = response.total
        }
        this.items
          .push(...response.results
            .map((catalog: Catalog) => {
              return this.createCatalogGridItem(catalog)
            })
          )
      })
      .catch((error) => {
        this.handlesAPIErrors('Error en carga de catálogos', error)
      })
      .finally(() => {
        this.loadingData = false
        if (this.showDomainFilter) {
          this.updatesDomainInfoAllRecords(this.items)
        }
        if (this.showProjectFilter) {
          this.updatesProjectInfoAllRecords(this.items)
        }
      })
  }
  /**
   * Gets the catalog that has property grouping enabled
   * and set it in selected grouping item
   *
   * @param pageRequest with the request configurations
   * @param updateTotalItem if the catalogs counter must be updated
   */
  async getLastGroupingCatalog () {
    const pageRequest: PageRequest<CatalogCatalogFilters> = new PageRequest<CatalogCatalogFilters>()
    pageRequest.size = 1
    pageRequest.filters = {
      sort: this.filters.sort,
      domain: this.filters.domain,
      grouping: true
    }
    CatalogsFillsService.getCatalogs(pageRequest).then(response => {
      if (response.results.length) {
        this.selectedGroupingItem = this.createCatalogGridItem(response.results[0])
      } else {
        this.selectedGroupingItem = new CatalogCatalogGridItem()
      }
    })
  }
  /**
   * Gets all the catalogs that can read the user
   * and sets it in allCatalogItems variable
   *
   * @param pageRequest with the request configurations
   * @param updateTotalItem if the catalogs counter must be updated
   */
  async getAllCatalogs (pageRequest: PageRequest<CatalogCatalogFilters>) {
    await CatalogsFillsService.getCatalogs(pageRequest)
      .then((response) => {
        this.allCatalogItems
          .push(...response.results
            .map((catalog: Catalog) => {
              return this.createCatalogGridItem(catalog)
            })
          )
      })
      .catch((error) => {
        this.handlesAPIErrors('Error en carga de catálogos', error)
      })

    if (this.allCatalogItems.length < this.totalItems) {
      pageRequest.page++
      await this.getAllCatalogs(pageRequest)
    }
  }

  /**
   * Gets all the domains and sets in 'domains' variable
   *
   * @param pageRequest for the request configurations
   */
  async getDomains (pageRequest: PageRequest<DomainCatalogFilters>) {
    this.loadingDomains = true
    const response = await CatalogsService.getDomains(pageRequest)
    this.domains
      .push(...response.results
        .map((domain: Domain) => {
          return new BasicEntity(domain.id, domain.name)
        })
      )
    if (this.domains.length < response.total) {
      pageRequest.page++
      await this.getDomains(pageRequest)
    } else {
      this.loadingDomains = false
    }
  }

  /**
   * Gets all the projects and sets in 'projects' class variable
   *
   * @param pageRequest for the request configurations
   */
  async getProjects (pageRequest: PageRequest<ProjectCatalogFilters>) {
    this.loadingProjects = true
    const response = await CatalogsService.getProjects(pageRequest)
    this.projects
      .push(...response.results
        .map((project) => {
          return { 'value': project.id, 'text': `${project.name}` }
        })
      )
    if (this.projects.length < response.total) {
      pageRequest.page++
      await this.getProjects(pageRequest)
    } else {
      this.loadingProjects = false
    }
  }

  /**
   * Obtains the domain records to inherit in the form views
   */
  get inheritDomains () {
    let userDomain = userInfoStore.userInfo.domain
    let domains = this.domains
      .map(d => { return { 'value': d.id, 'text': d.name } })
    if (!domains.some(d => d.value === userDomain.id)) {
      domains.push({ 'value': userDomain.id, 'text': userDomain.name })
    }
    return domains
  }

  /**
   * Obtains the project records to inherit in the form views
   */
  updateInheritProjects (projects: any[]) {
    this.inheritProjects = projects
  }

  /**
   * Updates the domain name for each catalog in the list
   * that is received as parameter
   *
   * @param catalogs to set their domain name
   */
  updatesDomainInfoAllRecords (catalogs: CatalogCatalogGridItem[]) {
    catalogs.forEach(catalog => {
      this.updateDomainInfo(catalog)
    })
  }

  /**
   * Updates the domain name for a specific catalog
   */
  updateDomainInfo (catalog: CatalogCatalogGridItem) {
    let findDomain = this.domains
      .find((d: BasicEntity) => d.id === catalog.domain)
    findDomain
      ? Vue.set(catalog, 'domainName', findDomain.name)
      : Vue.set(catalog, 'domainName', 'Sin dominio')
  }

  /**
   * Updates the project name for each catalog in the list
   * that is received as parameter
   *
   * @param catalogs to set their project name
   */
  updatesProjectInfoAllRecords (catalogs: CatalogCatalogGridItem[]) {
    catalogs.forEach(catalog => {
      this.updatesProjectInfo(catalog)
    })
  }

  /**
   * Updates the project name for a specific catalog
   */
  updatesProjectInfo (catalog: CatalogCatalogGridItem) {
    let findProject = this.projects
      .find((p: any) => p.value === catalog.project)
    findProject
      ? Vue.set(catalog, 'projectName', findProject.text)
      : catalog.generalDomain
        ? Vue.set(catalog, 'projectName', 'Catálogo de Dominio')
        : Vue.set(catalog, 'projectName', 'Sin proyecto')
  }

  /**
   * Sets the current selected item as the last
   * selected item
   *
   * @param item row in the table
   */
  selectItem (item: CatalogCatalogGridItem) {
    switch (this.currentAction) {
      case CrudActions.READ:
      case CrudActions.READ_DETAIL:
      case CrudActions.UPDATE:
      case CrudActions.DELETE:
        this.selectedItem = item
        break
      case CrudActions.CREATE:
        this.selectedItem = this.canUpdateCatalogs ? item : this.selectedItem
        this.currentAction = CrudActions.UPDATE
        // this.selectedItem = new CatalogCatalogGridItem()
        break
    }
  }

  /** Enable the field for the form view */
  enableForm () {
    this.readonly = false
  }

  /**
   * Sets the current action to display the view list
   */
  readItem () {
    this.currentAction = CrudActions.READ
    this.readonly = true
    this.inheritProjects = []
  }

  /**
   * Sets the current action to display the view form
   * for create a new catalog
   */
  createItem () {
    this.currentAction = CrudActions.CREATE
  }

  /**
   * Sets the current action to display the view form
   * for consult a selected catalog
   */
  queryItem () {
    this.currentAction = CrudActions.READ_DETAIL
  }

  /**
   * Sets the current action to display the view form
   * for edit a selected catalog
   */
  modifyItem () {
    this.currentAction = CrudActions.UPDATE
  }

  /**
   * Sets the current action to display the view form
   * for delete a selected catalog
   */
  deleteItem () {
    this.$bvModal.show('catalog-modal-delete')
  }

  /**
   * Sets the current action to display the view form
   * for activate grouping a selected catalog
   */
  activateGroupingItem () {
    this.sameGroupingSelectedItem = this.selectedGroupingItem.id === this.selectedItem.id
    this.groupingConfirmation = false
    this.$bvModal.show('catalog-modal-grouping')
  }
  /**
   * Deletes the last selected catalog
   */
  async deleteCatalog () {
    let catalogId = this.selectedItem.id
    let catalogName = this.selectedItem.name
    await CatalogsFillsService.deleteCatalog(catalogId)
      .then(() => {
        this.$bvToast.toast(
          `El catálogo: "${catalogId} - ${catalogName}" ha sido eliminado de forma adecuada.`,
          {
            title: 'Eliminación exitosa',
            variant: 'success',
            toaster: 'b-toaster-top-right',
            solid: true,
            autoHideDelay: 4500
          }
        )
        let itemRemovedIndex = this.items.findIndex(it => it.id === catalogId)
        this.items.splice(itemRemovedIndex, 1)
      })
      .catch((error) => {
        this.handlesAPIErrors('Error al eliminar', error)
      })
      .finally(() => {
        this.closeDeleteModal()
      })
  }

  /**
   * Update the last selected catalog
   */
  async updateCatalogGrouping () {
    if (this.selectedGroupingItem.id !== undefined) {
      let lastCatalogGrouping = {
        id: this.sameGroupingSelectedItem ? this.selectedItem.id : this.selectedGroupingItem.id,
        grouping: false
      } as Catalog
      await CatalogsFillsService.updateCatalog(lastCatalogGrouping).then(() => {
        this.$bvToast.toast(
          `Se ha quitado el agrupamiento del catálogo: "${lastCatalogGrouping.id} - ${this.selectedGroupingItem.name}" correctamente.`,
          {
            title: 'Actualización correcta',
            variant: 'success',
            toaster: 'b-toaster-top-right',
            solid: true,
            autoHideDelay: 4500
          }
        )
        const indexSelectedGrouping = this.items.findIndex(item => item.id === lastCatalogGrouping.id)
        if (indexSelectedGrouping >= 0) {
          this.items[indexSelectedGrouping].grouping = false
        }
        this.selectedGroupingItem = new CatalogCatalogGridItem()
      }).catch(error => {
        this.handlesAPIErrors('Error al desactivar el agrupamiento de catálogo.', error)
      })
    }
    if (!this.sameGroupingSelectedItem) {
      let lastCatalog = {
        id: this.selectedItem.id,
        grouping: true
      } as Catalog
      await CatalogsFillsService.updateCatalog(lastCatalog).then(() => {
        this.$bvToast.toast(
          `Se activo el agrupamiento en el catálogo: "${lastCatalog.id} - ${this.selectedItem.name}" correctamente.`,
          {
            title: 'Actualización correcta',
            variant: 'success',
            toaster: 'b-toaster-top-right',
            solid: true,
            autoHideDelay: 4500
          }
        )
        const indexSelected = this.items.findIndex(item => item.id === lastCatalog.id)
        this.items[indexSelected].grouping = true
        this.selectedGroupingItem = this.selectedItem
      }).catch(error => {
        this.handlesAPIErrors('Error al activar el agrupamiento de catálogo.', error)
      })
    }
    this.closeGroupingModal()
  }

  /**
   * Closes the modal for delete a catalog
   */
  closeDeleteModal () {
    this.$bvModal.hide('catalog-modal-delete')
    this.currentAction = CrudActions.READ
  }

  /**
   * Closes the modal for delete a catalog
   */
  closeGroupingModal () {
    this.$bvModal.hide('catalog-modal-grouping')
    this.currentAction = CrudActions.READ
  }

  /**
   * Creates a new row for the catalogs table and updates its domain and project info
   * @param catalog
   */
  addNewItem (catalog: Catalog) {
    let newCatalog = this.createCatalogGridItem(catalog)
    this.updateDomainInfo(newCatalog)
    this.updatesProjectInfo(newCatalog)
    this.items.unshift(newCatalog)
    this.selectedItem = newCatalog
  }

  /**
   * Updates the row on the catalogs table
   * @param catalog
   */
  updatesItem (catalog: Catalog) {
    let catalogIndex = this.items.findIndex(it => it.id === catalog.id)
    this.items.splice(catalogIndex, 1)
    this.addNewItem(catalog)
  }

  /**
   * Sets the selected row as the current catalog
   * and sets the action to display the form
   *
   * @param item selected row
   */
  selectItemAndShowDetail (item: CatalogCatalogGridItem) {
    this.selectItem(item)
    this.queryItem()
  }

  /**
   * @param item row selected
   * @returns true if the last row selected is the current one selected,
   * otherwise false
   */
  isItemSelected (item: CatalogCatalogGridItem) {
    return item.id === this.selectedItem.id
  }

  /**
   * Updates the value for the domain query param for catalogs
   * and reloads the projects based on this last domain.
   *
   * @param domain selected
   */
  updateDomain (domain: BasicEntity) {
    if (domain.id !== this.projectFilters.domain) {
      this.filters.domain = domain
      this.defaultDomain = domain
      this.filters.project = -1
      this.projectFilters.domain = domain.id
      this.loadProjects()
    }
  }

  /**
   * Apply the current filters and
   * generates a new request
   */
  applyFilters () {
    this.loadData(true)
  }

  /** Clears all filters and reloads data */
  cleanFilters () {
    let lastDomain = this.filters.domain
    this.filters = new CatalogCatalogFilters()
    this.filters.domain = lastDomain
    this.resetPaginator()
    this.loadData(true)
  }

  /** Clears the current selected item */
  cleanItemSelected () {
    this.selectedItem = new CatalogCatalogGridItem()
  }

  /** Clears all filters */
  isRowVisibleInPage (rowIndex: number) {
    return rowIndex >= this.visibleItems[0] && rowIndex < this.visibleItems[1]
  }

  /**
   * Makes a local sort in the current pagination
   * by the column selected
   *
   * @param col column the selected
   */
  sortItems (col: CatalogGridItemColumn) {
    CatalogsService.sortItems(this.items, col)
  }

  /**
   * Handles the event when the paginator changes
   *
   * @param page index
   */
  onPageChange (page: number) {
    this.cleanItemSelected()
    this.currentPageIndex = page
    this.loadData(false)
  }

  /**
   * Resets the values for the paginator
   */
  resetPaginator () {
    this.paginatorKey = new Date().getTime()
    this.cleanItemSelected()
    this.currentPageIndex = 1
  }

  /**
   * Handles a message error as response by the APIs
   * to show its details
   *
   * @param title for the error
   * @param error responsed by the apis
   * @param message (optional) for an extra error detail
   */
  handlesAPIErrors (title: string, error: any, message?: string) {
    let messageValues = {
      title: title,
      message: message || '',
      delay: 10000,
      error: error
    }
    let errorComponent = new MessageError({ propsData: messageValues })
    errorComponent.displayMessage()
  }

  async exportData () {
    this.allCatalogItems = []
    let pageRequest = new PageRequest<CatalogCatalogFilters>(this.PAGE_SIZE_CATALOGS)
    pageRequest.page = 0
    pageRequest.filters = this.filters
    await this.getAllCatalogs(pageRequest)
      .then(() => {
        if (this.showDomainFilter) {
          this.updatesDomainInfoAllRecords(this.allCatalogItems)
        }
        if (this.showProjectFilter) {
          this.updatesProjectInfoAllRecords(this.allCatalogItems)
        }
      })
      .finally(() => {
        this.printCsv()
      })
  }

  /**
   * Create an xlsx workbook to download the content of the selected catalog
   */
  downloadCatalog (): void {
    CatalogsFillsService.getCatalog(this.selectedItem.id)
      .then((response) => {
        const content = response.data && response.data.content ? response.data.content : []
        content.unshift(response.data && response.data.headers ? response.data.headers : [])
        let csv: string = ''
        for (const row of content) {
          csv += row.join(',') + '\n'
        }
        let element = this.$refs.csvDownload as any
        element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
        element.target = '_blank'
        element.download = `${this.selectedItem.name} (v${this.selectedItem.version}).csv`
        element.click()
      })
  }

  /**
   * Downloads a CSV file with the catalog records
   */
  printCsv () {
    let csv = this.columns
      .map((col) => {
        return ReportsService.formatTextForCsvFile(col.name)
      })
      .join(',') + '\n'

    for (let catalog of this.allCatalogItems) {
      csv += `${ReportsService.formatTextForCsvFile(catalog.name)},`
      csv += `${ReportsService.formatNullableTextForCsvFile(catalog.id)},`
      if (this.showDomainFilter) {
        csv += `${ReportsService.formatTextForCsvFile(catalog.domainName)},`
      }
      if (this.showProjectFilter) {
        csv += `${ReportsService.formatTextForCsvFile(catalog.projectName)},`
      }
      csv += `${ReportsService.formatNullableTextForCsvFile(catalog.version)},`
      csv += `${ReportsService.formatTextForCsvFile(catalog.description)},`
      csv += `${ReportsService.formatNullableTextForCsvFile(catalog.source)},`
      csv += `${ReportsService.formatNullableTextForCsvFile(catalog.creationDate)},`
      csv += `${ReportsService.formatNullableTextForCsvFile(catalog.updateDate)}\n`
    }
    let element = this.$refs.csvDownload as any
    element.href = 'data:text/csv;charset=utf-8,' + encodeURI(csv)
    element.target = '_blank'
    element.download = `catalogos.csv`
    element.click()
  }
}
